import { Link } from "gatsby"
import React, { useState, useEffect, useCallback } from "react"
import axios from "axios"
import Layout from "../components/App/Layout"
import Navbar from "../components/App/Navbar"
import PageBanner from "../components/Common/PageBanner"
import Footer from "../components/App/Footer"
import SEO from "../components/App/SEO"
import { Row, Col, Card, ListGroup } from "react-bootstrap"
import { Typeahead } from "react-bootstrap-typeahead"

const CATEGORY_TO_REGION = {
  1: "Metro",
  2: "Non-Metro",
  3: "Regional",
  4: "Inner City",
}

const LocationChecker = () => {
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [query, setQuery] = useState("")
  const [inputValue, setInputValue] = useState("")
  const [debouncedQuery, setDebouncedQuery] = useState("")
  const [selectedRecord, setSelectedRecord] = useState(null)

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(inputValue)
    }, 500)

    return () => clearTimeout(timer)
  }, [inputValue])

  const fetchLocationData = useCallback(async (searchQuery = "") => {
    try {
      setLoading(true)
      setError(null)

      const response = await axios.get(
        `${process.env.GATSBY_OLAP_BASE_URL}/api/postcode/filter?query=${searchQuery}&limit=20`
      )
      console.log("API response:", response.data)
      const locationData = response.data
      setData(locationData)
    } catch (err) {
      console.error("Error fetching location data:", err)
      setError("Failed to load location data. Please try again later.")
      setData([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (debouncedQuery.length >= 3) {
      fetchLocationData(debouncedQuery)
    } else {
      setData([])
      setLoading(false)
    }
  }, [debouncedQuery, fetchLocationData])

  return (
    <Layout>
      <SEO
        title="SMSF Property Location Checker"
        description="Quickly Search Australian Postcodes and Suburbs for SMSF Property Investments"
      />
      <Navbar />
      <PageBanner
        pageTitle="SMSF Property Location Checker"
        pageSubTitle="Use our quick and easy location checker to find the correct postcode or suburb in Australia. This tool is designed to help you identify suitable locations for your SMSF property investments."
      />

      <section className="wr-location-checker">
        <div className="container">
          {/* Error message */}
          {error && (
            <div className="alert alert-warning" role="alert">
              {error}
            </div>
          )}

          {/* Search bar */}
          <div className="form-group">
            {/* <input
              type="text"
              className="form-control"
              placeholder="Search by suburb or postcode"
              value={query}
              onChange={e => setQuery(e.target.value)}
              style={{ padding: "0.5rem", width: "100%", maxWidth: "400px" }}
            /> */}
            <h5 className="screen-reader-text">Search for:</h5>
            <div className="input-group search-field">
              <div className="input-group-prepend">
                <span className="input-group-text">
                  <i className="bx bx-search-alt"></i>
                </span>
              </div>
              <Typeahead
                id="location-typeahead"
                className="form-control"
                labelKey={option => {
                  const suburbs =
                    option.suburbs && option.suburbs.length > 0
                      ? option.suburbs.join(", ")
                      : "Unknown"
                  return `${suburbs} ${option.postCode} ${option.state}`
                }}
                options={data}
                placeholder="Search by suburb or postcode"
                inputValue={query}
                emptyLabel={loading ? "Searching..." : "No results found"}
                onInputChange={text => {
                  setQuery(text)
                  setInputValue(text)
                  if (selectedRecord) {
                    setSelectedRecord(null)
                  }
                }}
                onChange={selected => {
                  if (selected.length > 0) {
                    const item = selected[0]
                    const suburbs =
                      item.suburbs && item.suburbs.length > 0
                        ? item.suburbs.join(", ")
                        : "Unknown"
                    const displayText = `${suburbs} ${item.postCode} ${item.state}`
                    setQuery(displayText)
                    // Don't update inputValue here to prevent API call
                    setSelectedRecord(item)
                  }
                }}
                selected={selectedRecord ? [selectedRecord] : []}
                filterBy={() => true}
                minLength={3}
                useCache={false}
              />
            </div>
          </div>
        </div>

        {selectedRecord && (
          <>
            <div className="container">
              <div className="table-responsive-sm">
                <table className="table table-condensed mt-4 wr-location-checker-table">
                  <thead style={{ backgroundColor: "#f8f9fa" }}>
                    <tr>
                      <th className="text-left font-weight-bold py-2 px-4 align-middle">
                        Suburb
                      </th>
                      <th className="text-left font-weight-bold py-2 px-4 align-middle">
                        Postcode
                      </th>
                      <th className="text-left font-weight-bold py-2 px-4 align-middle">
                        State
                      </th>
                      <th className="text-left font-weight-bold py-2 px-4 align-middle">
                        Region Category
                      </th>
                      <th className="text-left font-weight-bold py-2 px-4 align-middle">
                        Local Government Area
                      </th>
                      <th className="text-left font-weight-bold py-2 px-4 align-middle">
                        Risk Category
                      </th>
                      <th className="text-left font-weight-bold py-2 px-4 align-middle">
                        High Density Category​
                      </th>
                      <th className="text-left font-weight-bold py-2 px-4 align-middle">
                        Banned Location​
                      </th>
                      <th className="text-left font-weight-bold py-2 px-4 align-middle">
                        Maximum Loan Size
                      </th>
                      <th className="text-left font-weight-bold py-2 px-4 align-middle">
                        Maximum LVR​​
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedRecord ? (
                      <tr>
                        <td className="text-left font-weight-normal py-2 px-4 align-middle">
                          {selectedRecord.suburbs &&
                          selectedRecord.suburbs.length > 0 ? (
                            <ul className="list-unstyled mb-0">
                              {selectedRecord.suburbs.map(
                                (suburb, subIndex) => (
                                  <li key={subIndex} className="mb-1">
                                    {suburb}
                                  </li>
                                )
                              )}
                            </ul>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td className="text-left font-weight-normal py-2 px-4 align-middle">
                          {selectedRecord.postCode}
                        </td>
                        <td className="text-left font-weight-normal py-2 px-4 align-middle">
                          {selectedRecord.state}
                        </td>
                        <td className="text-left font-weight-normal py-2 px-4 align-middle">
                          {selectedRecord.category ? (
                            CATEGORY_TO_REGION[selectedRecord.category] ||
                            `Category ${selectedRecord.category}`
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td className="text-left font-weight-normal py-2 px-4 align-middle">
                          {selectedRecord.lga}
                        </td>
                        <td className="text-left font-weight-normal py-2 px-4 align-middle">
                          {selectedRecord.additionalCare ? "Yes" : "No"}
                        </td>
                        <td className="text-left font-weight-normal py-2 px-4 align-middle">
                          {selectedRecord.highDensity ? "Yes" : "No"}
                        </td>
                        <td className="text-left font-weight-normal py-2 px-4 align-middle">
                          {selectedRecord.restricted ? "Yes" : "No"}
                        </td>
                        <td className="text-left font-weight-normal py-2 px-4 align-middle">
                          {(() => {
                            if (!selectedRecord.category)
                              return <span className="text-muted">-</span>

                            // Regional = Category 3 gets $3,000,000
                            if (selectedRecord.category === 3) {
                              return "$3,000,000"
                            }
                            // Inner-City, Metro or Non-Metro = Categories 1, 2, 4 get $3,500,000
                            else if (
                              [1, 2, 4].includes(selectedRecord.category)
                            ) {
                              return "$3,500,000"
                            }
                            // Fallback for unknown categories
                            else {
                              return <span className="text-muted">-</span>
                            }
                          })()}
                        </td>
                        <td className="text-left font-weight-normal py-2 px-4 align-middle">
                          {(() => {
                            if (!selectedRecord.category)
                              return <span className="text-muted">-</span>

                            // Regional = Category 3 gets 70% Residential / 65% Commercial
                            if (selectedRecord.category === 3) {
                              return (
                                <>
                                  <span>70% Residential</span>
                                  <br />
                                  <span>65% Commercial</span>
                                </>
                              )
                            }
                            // Inner-City, Metro or Non-Metro = Categories 1, 2, 4 get 80% both
                            else if (
                              [1, 2, 4].includes(selectedRecord.category)
                            ) {
                              return (
                                <>
                                  <span>80% Residential</span>
                                  <br />
                                  <span>80% Commercial</span>
                                </>
                              )
                            }
                            // Fallback for unknown categories
                            else {
                              return <span className="text-muted">-</span>
                            }
                          })()}
                        </td>
                      </tr>
                    ) : (
                      <tr>
                        <td colSpan={10} className="text-left py-4">
                          {selectedRecord ? (
                            <span className="text-muted">
                              No record selected
                            </span>
                          ) : (
                            <span className="text-muted">
                              {query.length === 0
                                ? "Start typing to search for suburbs or postcodes..."
                                : query.length < 3
                                ? "Please enter at least 3 characters to search"
                                : "No results found. Please select a location from the dropdown."}
                            </span>
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Conditional Info Cards based on selected results */}
            <div className="container">
              <Row xs={1} md={2} lg={3} className="g-4">
                {selectedRecord && (
                  <React.Fragment>
                    {/* If banned location is Yes, only show Banned Location card */}
                    {selectedRecord.restricted ? (
                      <Col>
                        <Card className="mx-auto mt-4">
                          <Card.Body>
                            <Card.Title className="mb-3">
                              Banned Location
                            </Card.Title>
                            <Card.Text>
                              Oxygen is not able to lend to properties in this
                              location.
                            </Card.Text>
                          </Card.Body>
                        </Card>
                      </Col>
                    ) : (
                      <>
                        {selectedRecord.highDensity && (
                          <Col>
                            <Card className="mx-auto mt-4">
                              <Card.Body>
                                <Card.Title className="mb-3">
                                  High Density Postcode Properties
                                </Card.Title>
                                <Card.Text>
                                  A high-density postcode property is a strata
                                  titled apartment which forms part of a
                                  development comprising more than 100
                                  apartments in high density postcodes. A
                                  development can comprise more than one (1)
                                  tower to reach the 100 apartments.
                                </Card.Text>
                                <ListGroup variant="flush" className="mt-3">
                                  <ListGroup.Item>
                                    <strong>Restrictions & Conditions:</strong>
                                  </ListGroup.Item>
                                  <ListGroup.Item>
                                    (i) Maximum LVR for residential properties
                                    is <strong>90%</strong>.
                                  </ListGroup.Item>
                                  <ListGroup.Item>
                                    (ii) Maximum exposure is limited to{" "}
                                    <strong>10%</strong> of the development.
                                  </ListGroup.Item>
                                  <ListGroup.Item>
                                    (iii) At least 2 of the comparable sales in
                                    the valuation report must be from similar
                                    apartments outside the actual development.
                                    Only resales within the complex should form
                                    part of the additional comparable sales.
                                  </ListGroup.Item>
                                  <ListGroup.Item>
                                    (iv) General commentary from the valuer to
                                    be noted for oversupply and two-tier
                                    marketing if applicable.
                                  </ListGroup.Item>
                                  <ListGroup.Item>
                                    (v) Minimum floor size <strong>30m²</strong>{" "}
                                    excluding balcony & car spaces with at least
                                    one bedroom separate from the living areas.
                                  </ListGroup.Item>
                                </ListGroup>
                              </Card.Body>
                              <Card.Footer>
                                <small>
                                  If in doubt, seek Oxygen confirmation on
                                  property prior to application.
                                </small>
                              </Card.Footer>
                            </Card>
                          </Col>
                        )}

                        {selectedRecord.category === 3 && (
                          <Col>
                            <Card className="mx-auto mt-4">
                              <Card.Body>
                                <Card.Title className="mb-3">
                                  Regional Location and Unclassified Locations
                                </Card.Title>
                                <ListGroup variant="flush">
                                  <ListGroup.Item>
                                    <strong>Restrictions & Conditions:</strong>
                                  </ListGroup.Item>
                                  <ListGroup.Item>
                                    (i) Maximum LVR for residential properties
                                    is <strong>70%</strong>.
                                  </ListGroup.Item>
                                  <ListGroup.Item>
                                    (ii) Maximum LVR for commercial properties
                                    is <strong>65%</strong>.
                                  </ListGroup.Item>
                                  <ListGroup.Item>
                                    (iii) Where the town size population is
                                    greater than 10,000 or the subject property
                                    is within 25km radius of a major regional
                                    town with a population greater than 50,000,
                                    the maximum LVR for residential properties
                                    is <strong>80%</strong>.
                                  </ListGroup.Item>
                                </ListGroup>
                              </Card.Body>
                              <Card.Footer>
                                <small>
                                  Seek confirmation from Oxygen for the 80% LVR
                                  prior to application submission.
                                </small>
                              </Card.Footer>
                            </Card>
                          </Col>
                        )}

                        {selectedRecord.additionalCare && (
                          <Col>
                            <Card className="mx-auto mt-4">
                              <Card.Body>
                                <Card.Title className="mb-3">
                                  High Risk Postcode Properties
                                </Card.Title>
                                <ListGroup variant="flush">
                                  <ListGroup.Item>
                                    (i) Some lending restrictions may apply to
                                    this area.
                                  </ListGroup.Item>
                                  <ListGroup.Item>
                                    (ii) Maximum LVR for residential and
                                    commercial properties remain at{" "}
                                    <strong>80%</strong>.
                                  </ListGroup.Item>
                                  <ListGroup.Item>
                                    Ask Oxygen to confirm property type &
                                    location prior to submitting application.
                                  </ListGroup.Item>
                                </ListGroup>
                              </Card.Body>
                              <Card.Footer>
                                <small>
                                  Ask Oxygen to confirm property type & location
                                  prior to submitting application.
                                </small>
                              </Card.Footer>
                            </Card>
                          </Col>
                        )}
                      </>
                    )}
                  </React.Fragment>
                )}
              </Row>
            </div>
          </>
        )}

        <div className="container text-center p-5">
          <Link to="/" className="default-btn primary-btn">
            Return Home
          </Link>
        </div>
      </section>
      <Footer />
    </Layout>
  )
}

export default LocationChecker
